import { useState, useRef, useEffect } from 'react';
import type { Server } from '../api/types.js';
import { useReloadSecrets, useSyncSchedules } from '../hooks/use-server-actions.js';

interface ServerMenuProps {
  server: Server;
}

export function ServerMenu({ server }: ServerMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const reloadSecrets = useReloadSecrets();
  const syncSchedules = useSyncSchedules();

  const isRunning = server.status === 'running';

  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function handleReloadSecrets(e: React.MouseEvent) {
    e.stopPropagation();
    setOpen(false);
    reloadSecrets.mutate(server.id);
  }

  function handleSyncSchedules(e: React.MouseEvent) {
    e.stopPropagation();
    setOpen(false);
    syncSchedules.mutate(server.id);
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="flex h-7 w-7 items-center justify-center rounded-md transition-colors"
        style={{
          color: 'var(--color-text-3)',
          backgroundColor: open ? 'var(--color-surface-2)' : 'transparent',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.backgroundColor = 'var(--color-surface-2)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.backgroundColor = 'transparent'; }}
        aria-label="Server actions"
        aria-expanded={open}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>

      {open && (
        <div
          className="animate-scale-in absolute right-0 top-full z-50 mt-1 min-w-[180px] overflow-hidden rounded-lg border shadow-lg"
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border-2)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
          }}
        >
          <div
            className="px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-widest"
            style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}
          >
            Actions
          </div>
          <MenuItem
            label="Reload secrets"
            disabled={!isRunning}
            pending={reloadSecrets.isPending}
            onClick={handleReloadSecrets}
            icon={<KeyIcon />}
          />
          <MenuItem
            label="Sync schedules"
            disabled={!isRunning}
            pending={syncSchedules.isPending}
            onClick={handleSyncSchedules}
            icon={<ClockIcon />}
          />
        </div>
      )}
    </div>
  );
}

function MenuItem({ label, disabled, pending, onClick, icon }: {
  label: string;
  disabled: boolean;
  pending: boolean;
  onClick: (e: React.MouseEvent) => void;
  icon: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors disabled:opacity-40"
      style={{ color: 'var(--color-text-2)' }}
      onMouseEnter={(e) => {
        if (!e.currentTarget.disabled) {
          e.currentTarget.style.backgroundColor = 'var(--color-surface-2)';
          e.currentTarget.style.color = 'var(--color-text)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = 'var(--color-text-2)';
      }}
    >
      <span className="flex h-4 w-4 items-center justify-center" style={{ opacity: 0.7 }}>
        {pending ? <SpinnerSmall /> : icon}
      </span>
      {label}
      {!disabled ? null : (
        <span
          className="ml-auto text-[9px] uppercase tracking-wider"
          style={{ color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}
        >
          off
        </span>
      )}
    </button>
  );
}

function KeyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function SpinnerSmall() {
  return (
    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
